/**
 * Hook for managing similar jobs functionality
 * 
 * Provides real-time similar job detection and caching
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { findSimilarJobs } from '../utils/jobSimilarityMatcher';
import { useAuth } from './useAuth';

export const useSimilarJobs = ({
  currentJobId,
  currentResults,
  autoLoad = true,
  searchOptions = {}
} = {}) => {
  const { userId } = useAuth();
  
  // State management
  const [state, setState] = useState({
    similarJobs: [],
    loading: false,
    error: null,
    lastSearched: null,
    searchOptions: null
  });

  // Cache and request management
  const cacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);

  // Memoize default options to prevent infinite loops
  const defaultOptions = useMemo(() => ({
    maxResults: 10,
    minSimilarityScore: 0.3,
    includeCurrentCasefile: false,
    searchRadius: 'user', // Start with user's jobs
    userId,
    ...searchOptions
  }), [userId, JSON.stringify(searchOptions)]);

  /**
   * Find similar jobs for the current job
   */
  const findSimilar = useCallback(async (options = {}) => {
    if (!currentJobId || !currentResults) {
      setState(prev => ({ ...prev, similarJobs: [], error: null }));
      return [];
    }

    // Check cache first
    const cacheKey = `${currentJobId}-${JSON.stringify(options)}`;
    if (cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setState(prev => ({ 
        ...prev, 
        similarJobs: cached,
        lastSearched: Date.now(),
        searchOptions: options
      }));
      return cached;
    }

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const mergedOptions = { ...defaultOptions, ...options };
      
      const similarJobs = await findSimilarJobs(
        currentJobId,
        currentResults,
        mergedOptions
      );

      // Cache the results
      cacheRef.current.set(cacheKey, similarJobs);

      setState(prev => ({
        ...prev,
        similarJobs,
        loading: false,
        lastSearched: Date.now(),
        searchOptions: mergedOptions
      }));

      return similarJobs;

    } catch (error) {
      console.error('Error finding similar jobs:', error);
      setState(prev => ({
        ...prev,
        similarJobs: [],
        loading: false,
        error: error.message
      }));
      return [];
    }
  }, [currentJobId, currentResults, userId, defaultOptions]);

  /**
   * Expand search to include more jobs
   */
  const expandSearch = useCallback(async (newRadius = 'all') => {
    const expandedOptions = {
      ...state.searchOptions,
      searchRadius: newRadius,
      maxResults: Math.min((state.searchOptions?.maxResults || 10) * 2, 50)
    };

    return await findSimilar(expandedOptions);
  }, [findSimilar, state.searchOptions]);

  /**
   * Refresh similar jobs (clear cache and search again)
   */
  const refresh = useCallback(async () => {
    // Clear cache for this job
    const keysToDelete = [];
    for (const key of cacheRef.current.keys()) {
      if (key.startsWith(currentJobId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => cacheRef.current.delete(key));

    return await findSimilar(state.searchOptions || {});
  }, [currentJobId, findSimilar, state.searchOptions]);

  /**
   * Get similar jobs by specific criteria
   */
  const getSimilarByType = useCallback((matchType) => {
    return state.similarJobs.filter(job => 
      job.matchTypes.includes(matchType)
    );
  }, [state.similarJobs]);

  /**
   * Get high confidence matches only
   */
  const getHighConfidenceMatches = useCallback((minConfidence = 0.7) => {
    return state.similarJobs.filter(job => 
      job.confidence >= minConfidence
    );
  }, [state.similarJobs]);

  /**
   * Get statistics about similar jobs
   */
  const getStatistics = useCallback(() => {
    const jobs = state.similarJobs;
    
    if (jobs.length === 0) {
      return {
        total: 0,
        highConfidence: 0,
        exactMatches: 0,
        vehicleMatches: 0,
        plateMatches: 0,
        averageScore: 0
      };
    }

    const exactMatches = jobs.filter(job => 
      job.matchTypes.includes('exact_plate') || 
      job.matchTypes.includes('exact_vehicle')
    ).length;

    const vehicleMatches = jobs.filter(job => 
      job.matchTypes.some(type => type.includes('vehicle') || type.includes('make'))
    ).length;

    const plateMatches = jobs.filter(job => 
      job.matchTypes.some(type => type.includes('plate'))
    ).length;

    const highConfidence = jobs.filter(job => job.confidence >= 0.7).length;
    const averageScore = jobs.reduce((sum, job) => sum + job.similarityScore, 0) / jobs.length;

    return {
      total: jobs.length,
      highConfidence,
      exactMatches,
      vehicleMatches,
      plateMatches,
      averageScore: Math.round(averageScore * 100) / 100
    };
  }, [state.similarJobs]);

  // Auto-load similar jobs when job/results change
  useEffect(() => {
    if (autoLoad && currentJobId && currentResults) {
      findSimilar();
    }
  }, [autoLoad, currentJobId, currentResults, findSimilar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    similarJobs: state.similarJobs,
    loading: state.loading,
    error: state.error,
    lastSearched: state.lastSearched,
    
    // Actions
    findSimilar,
    expandSearch,
    refresh,
    
    // Utilities
    getSimilarByType,
    getHighConfidenceMatches,
    getStatistics,
    
    // State
    hasResults: state.similarJobs.length > 0,
    isEmpty: !state.loading && state.similarJobs.length === 0,
    searchOptions: state.searchOptions
  };
};

export default useSimilarJobs;
